// Mobile app side track - what the three app STEP pages share (F135).
//
// F135 (Pavel 2026-08-11): "v tom kroku číslo tři, první kroky k aplikaci, by
// vůbec nic nemělo být o dotazníku". Until this split ONE component rendered
// the whole track and gated its blocks by flags, so the Apple step showed the
// questionnaire card ("KROK 15 Z 17"), the Apple guide AND the release
// timeline ("KROK 17 Z 17") on one screen. Each app rail step is now its own
// page in its own file (K04/K05 + write-scope-is-feedback-unit): AppleStep,
// QuestionnaireStep, ReleaseStep. This module holds only what all three read.
import type { Locale, PartnerDto, ResourceDto, SalesStepDto, SectionDto } from "../../../shared/dto";
import type { AppPrefillState } from "../../../shared/dto";
import type { ActionIssue } from "../../action-error";
import { isRecord, resource } from "../shared";

export type AppAction = "apple-start";

export interface AppTrackProps {
  surface?: "app" | "app:apple" | "app:questionnaire" | "app:release";
  sections: SectionDto[];
  resources: ResourceDto[];
  salesSteps: SalesStepDto[];
  includesApp: boolean;
  membershipRole: "owner" | "collaborator";
  profilePhotoUrl: string | null;
  partner?: PartnerDto | null;
  locale: Locale;
  t: (key: string) => string;
  busyAction: string | null;
  actionError: ActionIssue | null;
  dataErrors?: Set<string>;
  /** F135b: the ONE server fact about a machine prefill. Absent = none is coming. */
  appPrefill?: AppPrefillState | null;
  onAction: (action: AppAction, payload?: Record<string, unknown>) => void | Promise<void>;
  csrfToken?: string;
  onChanged?: () => Promise<void>;
  // F118: the Apple step's mark is derived from the `step_confirmations`
  // RESOURCE, so reporting through onChanged alone leaves it unchanged on
  // screen. The router already passes this; the type is what makes it usable.
  onReloadData?: () => Promise<void>;
  /** F175: a pre-answered "do you already have an account?" (tests, deep links). */
  appleHasAccount?: "yes" | "no";
}

/**
 * The Apple facts every app page reads, derived once. `confirmed` is the S21
 * truth (the checklist went through, or the saved resource already says the
 * registration is submitted/active); `explicitlyConfirmed` is the separate
 * F118 client confirmation that carries the rail mark.
 */
export function appleFacts(props: AppTrackProps): {
  section: SectionDto | undefined;
  account: Record<string, unknown> | null;
  confirmed: boolean;
  explicitlyConfirmed: boolean;
} {
  const appState = resource(props.resources, "app_state")?.value;
  const confirmations = resource(props.resources, "step_confirmations")?.value;
  const section = props.sections.find((item) => item.key === "app");
  const account = isRecord(appState?.apple_account) ? appState.apple_account : null;
  return {
    section,
    account,
    confirmed: section?.state === "working"
      || section?.state === "done"
      || ["submitted", "active"].includes(String(account?.registration_status ?? "")),
    explicitlyConfirmed: isRecord(confirmations?.apple) && confirmations.apple.confirmed === true
  };
}

export function appStateValue(props: AppTrackProps): Record<string, unknown> | undefined {
  return resource(props.resources, "app_state")?.value;
}

// 🔴 `appReleasePhase` lived here and is DELETED, not merely unread (F111's
// law). It derived seven phases from `build`, `app_store_review` and
// `google_play_review` - three fields we invented, seeded `not_started` for
// every client and never written by anything. F137 replaced the whole idea with
// the platform's own lifecycle (`shared/app-release.ts`), derived once on the
// server. Leaving the function here would have been an invitation to render the
// store checklist again from the next surface that needed a phase.

/**
 * F144: the checklist's closed state and the sentence that explains it branch
 * on ONE fact, so a control can never refuse for a reason the page does not
 * print (the fix-partak-ovladace law). `busyAction` is deliberately NOT here:
 * a submit in flight is a WORKING state the button already narrates, not a
 * closed one that owes the reader a reason.
 */
export function appleChecklistLock(
  props: AppTrackProps,
  section: SectionDto | undefined
): { locked: boolean; reasonKey: string | null } {
  if (props.membershipRole !== "owner") return { locked: true, reasonKey: "app.check.ownerOnly" };
  // After F144's server fix this is a race (an admin turning the app on while
  // the client has the page open), never the resting state it used to be.
  if (section?.state !== "next") return { locked: true, reasonKey: "app.check.stepNotOpen" };
  return { locked: false, reasonKey: null };
}

const DUNS_TOKEN = "D-U-N-S";

/** The D-U-N-S code never breaks across lines mid-token (QC W1b). */
export function NonBreakingDunsText({ text }: { text: string }) {
  const parts = text.split(DUNS_TOKEN);
  return parts.map((part, index) => (
    <span key={`${index}:${part}`}>
      {index > 0 && <span className="no-break-token">{DUNS_TOKEN}</span>}
      {part}
    </span>
  ));
}
